import React from 'react';
import juniorImg from '../assets/JUNIOR SCHOOL.png';
import PageHero from './PageHero';
import {
  Users, Globe, FlaskConical, Cpu, BookOpen, Trophy,
  Layers, Lightbulb, Target, Clock, Badge, Info,
  CheckCircle, Microscope, Music, Dumbbell, Languages,
  Hash, Palette, Brain, Star
} from 'lucide-react';
import middleImg from '../assets/JUNIOR SCHOOL.png';
import primaryImg from '../assets/PRIMARY SCHOOL.png';

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="text-center mb-16">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
          <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">{eyebrow}</span>
        </div>
      )}
      <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy leading-tight uppercase">{title}</h2>
      {sub && <p className="text-ink/60 text-lg mt-4 max-w-2xl mx-auto">{sub}</p>}
    </div>
  );
}

function FeatureBox({ icon, title, desc, accent }) {
  const styles = {
    orange: { border: 'border-[#f97316]', bg: 'bg-[#fff7f0]', ic: 'text-[#f97316]' },
    blue: { border: 'border-[#3b82f6]', bg: 'bg-[#eff6ff]', ic: 'text-[#3b82f6]' },
    green: { border: 'border-[#22c55e]', bg: 'bg-[#f0fdf4]', ic: 'text-[#22c55e]' },
    red: { border: 'border-[#ef4444]', bg: 'bg-[#fef2f2]', ic: 'text-[#ef4444]' },
    pink: { border: 'border-[#ec4899]', bg: 'bg-[#fdf2f8]', ic: 'text-[#ec4899]' },
    purple: { border: 'border-[#a855f7]', bg: 'bg-[#faf5ff]', ic: 'text-[#a855f7]' },
    yellow: { border: 'border-[#eab308]', bg: 'bg-[#fefce8]', ic: 'text-[#eab308]' },
    violet: { border: 'border-[#8b5cf6]', bg: 'bg-[#f5f3ff]', ic: 'text-[#8b5cf6]' },
  }[accent];
  return (
    <div className={`rounded-2xl border-l-4 p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300 ${styles.border} ${styles.bg}`}>
      <div className={`mb-4 ${styles.ic}`}>{icon}</div>
      <h4 className="text-lg font-heading font-semibold text-navy mb-2">{title}</h4>
      <p className="text-ink/70 text-[15px] leading-relaxed">{desc}</p>
    </div>
  );
}

function SubjectFeature({ icon, title, desc, align = 'right' }) {
  const isLeft = align === 'left';
  return (
    <div className={`flex items-start gap-4 mb-10 ${isLeft ? 'flex-row-reverse text-right' : ''}`}>
      <div className="shrink-0 w-12 h-12 rounded-xl bg-navy flex items-center justify-center text-white shadow-md">
        {icon}
      </div>
      <div className={isLeft ? 'text-right' : ''}>
        <h3 className="text-lg font-heading font-semibold text-navy mb-1">{title}</h3>
        <p className="text-ink/70 text-[15px] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function InfoCard({ icon, iconBg, checkColor, title, items }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-line p-8 h-full">
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>{icon}</div>
        <h3 className="text-xl font-heading font-semibold text-navy">{title}</h3>
      </div>
      <ul className="space-y-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle size={18} className={`shrink-0 mt-0.5 ${checkColor}`} />
            <span className="text-ink/80 text-[15px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MiddleSchoolPage({ setPage, t }) {
  const navCategory = t?.nav?.[2]?.label || 'Academics';
  const homeLabel = t?.nav?.[0]?.label || 'Home';

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title={"Middle School"}
        breadcrumbs={[homeLabel, navCategory, "Middle School"]}
        eyebrow={navCategory}
        subtitle={"IB MYP — Grade 7 to Grade 10"}
        bgImage={juniorImg}
      />


      {/* ── About ── */}
      <div className="max-w-[1200px] mx-auto px-8 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">IB MYP · Grades 7–10</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy mb-6 leading-tight uppercase">
              Developing Thinkers, Leaders & Innovators
            </h2>
            <p className="text-ink/75 text-lg leading-relaxed mb-6">
              GMIS Jakarta's Middle School is grounded in the <strong>IB Middle Years Programme (MYP)</strong>, a challenging
              five-year framework that develops the intellectual, personal, emotional, and social skills students
              need to thrive in a rapidly changing world.
            </p>
            <p className="text-ink/75 text-lg leading-relaxed">
              Through interdisciplinary learning, service, and authentic assessment, MYP students build depth of
              knowledge alongside the communication, critical thinking, and leadership skills valued globally.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-gold/10 rounded-2xl" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-navy/10 rounded-full" />
            <img src={primaryImg} alt="GMIS Middle School" className="relative z-10 rounded-2xl shadow-2xl w-full h-[400px] object-cover object-center border-4 border-white" />
          </div>
        </div>
      </div>

      {/* ── Facilities ── */}
      <div className="bg-white py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="Campus & Resources" title="Facilities & Special Programs" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureBox accent="orange" icon={<Layers size={28} />}
              title="Interdisciplinary Units"
              desc="Structured units of inquiry connecting multiple subjects through real-world global contexts." />
            <FeatureBox accent="blue" icon={<FlaskConical size={28} />}
              title="Science & Tech Labs"
              desc="Fully equipped biology, chemistry, physics, and ICT labs for hands-on experimentation." />
            <FeatureBox accent="green" icon={<Globe size={28} />}
              title="Community & Service"
              desc="Mandatory service learning that connects students to local and global communities." />
            <FeatureBox accent="red" icon={<Brain size={28} />}
              title="ATL Skills Framework"
              desc="Approaches to Learning: thinking, communication, social, self-management, and research skills." />
            <FeatureBox accent="pink" icon={<Trophy size={28} />}
              title="Model UN & Debates"
              desc="Developing global perspective, public speaking, diplomacy, and argumentation." />
            <FeatureBox accent="purple" icon={<Music size={28} />}
              title="Arts & Performing Arts"
              desc="Visual arts, music, drama, and creative expression embedded across the MYP curriculum." />
            <FeatureBox accent="yellow" icon={<Dumbbell size={28} />}
              title="Physical & Health Education"
              desc="Team sports, individual wellness, and PHE units developing lifelong healthy habits." />
            <FeatureBox accent="violet" icon={<Cpu size={28} />}
              title="Design & Technology"
              desc="Design cycle projects, coding, robotics, and digital design for creative problem-solving." />
          </div>
        </div>
      </div>

      {/* ── MYP Subject Groups ── */}
      <div className="py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="IB MYP Framework" title="MYP Subject Groups" sub="Eight interdisciplinary subject groups — Grades 7 to 10" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div>
              <SubjectFeature align="left" icon={<Languages size={20} />}
                title="Language & Literature"
                desc="Analytical reading, creative writing, and literary appreciation in English." />
              <SubjectFeature align="left" icon={<Globe size={20} />}
                title="Language Acquisition"
                desc="Bahasa Indonesia, Mandarin, or French — building global communication competency." />
              <SubjectFeature align="left" icon={<Hash size={20} />}
                title="Mathematics"
                desc="Conceptual and extended mathematics — algebra, geometry, statistics, and problem-solving." />
              <SubjectFeature align="left" icon={<Globe size={20} />}
                title="Individuals & Societies"
                desc="History, geography, economics, and social studies through global contexts." />
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl scale-110" />
                <img src={middleImg} alt="MYP at GMIS" className="relative z-10 rounded-2xl shadow-2xl w-full max-w-[320px] h-[440px] object-cover object-top mx-auto border-4 border-white" />
              </div>
            </div>
            <div>
              <SubjectFeature align="right" icon={<FlaskConical size={20} />}
                title="Sciences"
                desc="Biology, chemistry, and physics explored through inquiry, experimentation, and research." />
              <SubjectFeature align="right" icon={<Palette size={20} />}
                title="Arts"
                desc="Visual arts, music, drama, and film — creative inquiry and artistic process celebrated." />
              <SubjectFeature align="right" icon={<Dumbbell size={20} />}
                title="Physical & Health Ed"
                desc="Teamwork, strategy, individual sport, and wellness — a balanced and healthy lifestyle." />
              <SubjectFeature align="right" icon={<Cpu size={20} />}
                title="Design"
                desc="Digital and product design projects using the design cycle to solve real problems." />
            </div>
          </div>
        </div>
      </div>

      {/* ── Info Cards ── */}
      {/* <div className="bg-white py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="Programme Details" title="Middle School Programme Overview" sub="Grades 7 to 10 — Empowering Students Through Inquiry and Action" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <InfoCard
              icon={<Lightbulb size={22} className="text-blue-600" />} iconBg="bg-blue-50" checkColor="text-blue-500"
              title="Approach & Teaching Methodology"
              items={[
                "IB MYP concept-based and context-driven learning across eight subject groups.",
                "Interdisciplinary units connecting subjects through globally significant themes.",
                "Approaches to Learning (ATL) skills embedded in every lesson and project.",
                "Personal Project in Grade 10 — a self-directed, in-depth individual research project.",
                "Formative and summative MYP criteria-based assessment aligned to IB standards.",
                "Experienced MYP-trained teachers who facilitate inquiry-led learning environments.",
              ]}
            />
            <InfoCard
              icon={<Target size={22} className="text-green-600" />} iconBg="bg-green-50" checkColor="text-green-500"
              title="Programme Focus"
              items={[
                "Deep understanding of content through conceptual inquiry, not memorisation.",
                "Service as Action — mandatory community engagement integrated throughout MYP.",
                "Strong preparation for IB Diploma and Cambridge IGCSE pathways in Grade 11.",
                "Leadership development through house system, student council, and committees.",
                "Developing internationally-minded, reflective learners who think critically and act ethically.",
              ]}
            />
            <InfoCard
              icon={<Clock size={22} className="text-cyan-600" />} iconBg="bg-cyan-50" checkColor="text-cyan-500"
              title="School Timings"
              items={[
                "Monday to Friday: 7:30 AM – 3:00 PM",
                "After-school activities and clubs available until 5:00 PM.",
                "Study hall and academic support sessions scheduled during the week.",
                "Examinations held at the end of each semester in alignment with the IB MYP calendar.",
              ]}
            />
            <InfoCard
              icon={<Badge size={22} className="text-amber-600" />} iconBg="bg-amber-50" checkColor="text-amber-500"
              title="Uniform & Dress Code"
              items={[
                "Smart GMIS uniform reflecting the school's values of pride and belonging.",
                "Separate PE/sports attire required for physical education and sports days.",
                "Formal uniform for school assemblies, presentations, and special events.",
                "Students are expected to maintain a neat and professional appearance at all times.",
              ]}
            />
          </div>
          <div className="bg-[#fef8f0] border border-gold/30 rounded-2xl p-8 shadow-sm mt-8">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Star size={18} className="text-gold" /> A Note to Parents
            </h3>
            <div className="flex flex-col gap-3">
              {[
                "Middle school is a critical developmental phase. Regular, open conversations with your child about their goals and wellbeing are just as important as academics.",
                "Encourage your child to embrace the IB Learner Profile values — being inquiring, open-minded, caring, and reflective — both inside and outside the classroom.",
                "The Personal Project in Grade 10 is a significant undertaking. Support your child's independence by encouraging them to manage their time and own their learning journey.",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 rounded-full bg-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                  </div>
                  <p className="text-ink/75 text-[14px] leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div> */}

      {/* ── CTA ── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-8 py-12">
        <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
          <div className="relative z-10">
            <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight uppercase">
              Prepare Your Child for a Global Future
            </h3>
            <p className="text-white/70 text-xl max-w-2xl mx-auto mb-10">
              Meet our MYP team and explore our world-class middle school facilities in person.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button onClick={() => { if (setPage) { setPage('contact'); window.scrollTo(0, 0); } }}
                className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300">
                Schedule a Visit
              </button>
              <button onClick={() => { if (setPage) { setPage('coming-soon'); window.scrollTo(0, 0); } }}
                className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20">
                Apply Now
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
